import React, { useEffect, useRef, useState } from 'react';
import { AdminSection } from './AdminPage';
import './AdminNavigation.css';

interface AdminNavigationProps {
  currentSection: AdminSection;
  onSectionChange: (section: AdminSection) => void;
  isMobileMenuOpen?: boolean;
  onOpenHelp?: () => void;
  onPreviewSite?: () => void;
  onLogout?: () => void;
}

type NavItem = { id: AdminSection; label: string };
type NavSubgroup = { title: string; items: NavItem[] };
type NavGroup = {
  id: string;
  title: string;
  items?: NavItem[];
  subgroups?: NavSubgroup[];
  /** Folded groups stay shut until opened (or until you are working inside one). */
  folded?: boolean;
};

/**
 * Studio is the working day. Website keeps galleries, the blog, and packages
 * in their own lists. Look is the visual site. Setup stays folded.
 */
const groups: NavGroup[] = [
  {
    id: 'studio',
    title: 'Studio',
    items: [
      { id: 'dashboard', label: 'Today' },
      { id: 'clients', label: 'Clients' },
      { id: 'client-messages', label: 'Client messages' },
      { id: 'bookings', label: 'Calendar' },
      { id: 'quote-history', label: 'Quotations' },
      { id: 'payments', label: 'Payments' },
      { id: 'finance', label: 'Finance' },
      { id: 'review-requests', label: 'Reviews' },
      { id: 'client-guides', label: 'Client guides' },
    ],
  },
  {
    id: 'website',
    title: 'Website',
    subgroups: [
      {
        title: 'Galleries',
        items: [
          { id: 'galleries', label: 'Galleries' },
          { id: 'categories', label: 'Gallery categories' },
          { id: 'display-settings', label: 'Gallery layout' },
        ],
      },
      {
        title: 'Blog',
        items: [
          { id: 'blogs', label: 'Blog' },
          { id: 'blog-categories', label: 'Blog categories' },
        ],
      },
      {
        title: 'Packages',
        items: [
          { id: 'packages', label: 'Packages' },
          { id: 'addons', label: 'Package add-ons' },
          { id: 'package-customization', label: 'Package text' },
        ],
      },
      {
        title: 'Also on the site',
        items: [
          { id: 'homepage', label: 'Homepage introduction' },
          { id: 'content', label: 'About Me' },
          { id: 'instagram', label: 'Instagram' },
          { id: 'search-photos', label: 'Photos for Google' },
        ],
      },
    ],
  },
  {
    id: 'look',
    title: 'Look',
    items: [
      { id: 'logo', label: 'Logo' },
      { id: 'theme', label: 'Colours' },
      { id: 'welcome-popup', label: 'Welcome popup' },
      { id: 'loading-animations', label: 'Loading animation' },
      { id: 'share-image', label: 'Package share image' },
    ],
  },
  {
    id: 'administration',
    title: 'Setup',
    folded: true,
    items: [
      { id: 'messaging', label: 'Message templates' },
      { id: 'studio-settings', label: 'Studio notifications' },
      { id: 'currency-management', label: 'Currency' },
      { id: 'seo', label: 'SEO' },
      { id: 'settings', label: 'Share message' },
      { id: 'backup-management', label: 'Backups' },
    ],
  },
];

const groupItems = (group: NavGroup) => [
  ...(group.items || []),
  ...(group.subgroups || []).flatMap((subgroup) => subgroup.items),
];

const groupHolding = (section: AdminSection) => (
  groups.find((group) => groupItems(group).some((item) => item.id === section))?.id || ''
);

const AdminNavigation: React.FC<AdminNavigationProps> = ({
  currentSection,
  onSectionChange,
  isMobileMenuOpen = false,
  onOpenHelp,
  onPreviewSite,
}) => {
  const firstItem = useRef<HTMLButtonElement | null>(null);
  const [opened, setOpened] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isMobileMenuOpen) firstItem.current?.focus();
  }, [isMobileMenuOpen]);

  // Working inside a folded group opens it, so you never lose your place.
  useEffect(() => {
    const holder = groupHolding(currentSection);
    if (holder) setOpened((current) => (current[holder] ? current : { ...current, [holder]: true }));
  }, [currentSection]);

  return (
    <nav className="studio-nav" aria-label="Studio">
      <div className="studio-nav-mark">
        <img src="/brand/carmen-lorraine-mark.svg" alt="" />
        <div>
          <p>Carmen Lorraine</p>
          <strong>Studio</strong>
        </div>
      </div>
      <div className="studio-nav-scroll">
        {groups.map((group, groupIndex) => {
          const isOpen = !group.folded || Boolean(opened[group.id]);
          const listId = `studio-nav-${group.id}`;
          return (
            <section key={group.id} data-nav={group.id}>
              {group.folded ? (
                <h2>
                  <button
                    type="button"
                    className="studio-nav-group-toggle"
                    aria-expanded={isOpen}
                    aria-controls={listId}
                    onClick={() => setOpened((current) => ({ ...current, [group.id]: !isOpen }))}
                  >
                    <span>{group.title}</span>
                    <span aria-hidden="true">{isOpen ? '−' : '+'}</span>
                  </button>
                </h2>
              ) : (
                <h2>{group.title}</h2>
              )}
              {isOpen ? (
                <div id={listId}>
                  {(group.subgroups || [{ title: '', items: group.items || [] }]).map((subgroup) => (
                    <div className={subgroup.title ? 'studio-nav-subgroup' : undefined} key={subgroup.title || group.id}>
                      {subgroup.title ? <h3>{subgroup.title}</h3> : null}
                      <ul>
                        {subgroup.items.map((item, itemIndex) => (
                          <li key={item.id}>
                            <button
                              ref={groupIndex === 0 && itemIndex === 0 && !subgroup.title ? firstItem : undefined}
                              type="button"
                              className={currentSection === item.id ? 'is-active' : ''}
                              aria-current={currentSection === item.id ? 'page' : undefined}
                              onClick={() => onSectionChange(item.id)}
                            >
                              {item.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
      <div className="studio-nav-utilities">
        <button type="button" onClick={onOpenHelp}>Help</button>
        <button type="button" onClick={onPreviewSite}>Preview site</button>
      </div>
    </nav>
  );
};

export default AdminNavigation;
